import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { Button, Modal, Spinner } from 'react-bootstrap'
import { useMutation, useQuery } from 'react-query'

import { useApi } from '../../../hooks'
import useAppContext from '../../app'
import { axios } from '../../../core/httpClient'
import toast from '../../toast'
import { FormSelectSearch } from '../../formControl'
import './adasReports.css'

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB

const UploadModal = ({ show, onHide, onSuccess }) => {
  const { adasReportService, mainService } = useApi()
  const { store: { clientSettings, featureFlags } } = useAppContext()
  // ADAS Map (OPUS) reports are offered only once the APIs take them
  // (featureFlags.adasMapImport); until then an ADAS Map PDF would go to adasThink.
  const adasMapEnabled = !!(featureFlags && featureFlags.adasMapImport)
  const [file, setFile] = useState(null)
  const [vendorId, setVendorId] = useState('')
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef(null)

  const { data: vendorsData } = useQuery(
    'adas-report-vendors',
    () => adasReportService.vendors(),
    { enabled: show, refetchOnWindowFocus: false }
  )
  const vendors = useMemo(() => (vendorsData && vendorsData.vendors) || [], [vendorsData])

  const reset = () => {
    setFile(null)
    setVendorId('')
    setUploading(false)
  }

  const handleClose = () => {
    reset()
    onHide()
  }

  const validateFile = (f) => {
    if (f.size > MAX_FILE_SIZE) {
      toast.error({ title: 'File too large', body: 'Maximum file size is 20MB.' })
      return false
    }
    if (f.type !== 'application/pdf') {
      toast.error({ title: 'Invalid file', body: 'Please upload a PDF file.' })
      return false
    }
    return true
  }

  const handleFileChange = (e) => {
    const selected = e.target.files && e.target.files[0]
    if (!selected) return
    if (validateFile(selected)) setFile(selected)
  }

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    const dropped = e.dataTransfer.files && e.dataTransfer.files[0]
    if (!dropped) return
    if (dropped.size > MAX_FILE_SIZE) {
      toast.error({ title: 'File too large', body: 'Maximum file size is 20MB.' })
      return
    }
    if (dropped.type !== 'application/pdf') {
      toast.error({ title: 'Invalid file', body: 'Please upload a PDF file.' })
      return
    }
    setFile(dropped)
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
  }, [])

  const submitMutation = useMutation(
    (payload) => adasReportService.upload(payload),
    {
      onSuccess: () => {
        toast.success({
          body: adasMapEnabled
            ? 'File uploaded. Report generation may take up to a minute.'
            : 'Repair estimate uploaded successfully. Report generation may take up to a minute.',
          className: 'toast-soft-success',
        })
        handleClose()
        if (onSuccess) onSuccess()
      },
      onError: (err) => {
        toast.error({ title: 'Upload Failed', body: err.message || 'Please try again.' })
        setUploading(false)
      },
    }
  )

  const handleSubmit = async () => {
    if (!file || !vendorId) return
    const companyId = clientSettings && clientSettings.company
    if (!companyId) {
      toast.error({ title: 'Upload Failed', body: 'Missing company context. Please sign in again.' })
      return
    }
    setUploading(true)
    try {
      // 1. Request a presigned S3 URL for the PDF (sign_s3 signs application/pdf)
      const signed = await mainService.uploadFile(companyId, {
        contentType: 'application/pdf',
        fileName: file.name,
      })
      const signedRequest = signed && signed.signed_request
      const fileURL = signed && signed.url
      if (!signedRequest || !fileURL) throw new Error('Could not get an upload URL.')

      // 2. PUT the file directly to S3. Headers must match the signed params.
      await axios.put(signedRequest, file, {
        headers: { 'Content-Type': 'application/pdf', 'x-amz-acl': 'public-read' },
      })

      // 3. Create the report from the uploaded estimate (parsing runs async API-side)
      submitMutation.mutate({
        vendorId,
        fileURL,
        fileName: file.name,
        calibrationReady: 'no',
      })
    } catch (err) {
      toast.error({ title: 'Upload Failed', body: (err && err.message) || 'Please try again.' })
      setUploading(false)
    }
  }

  // Auto-select vendor if only one is available
  useEffect(() => {
    if (vendors.length === 1 && !vendorId) setVendorId(vendors[0]._id)
  }, [vendors, vendorId])

  const vendorOptions = vendors.map(v => ({ value: v._id, label: v.name }))
  const canSubmit = !!file && !!vendorId

  return (
    <Modal show={show} onHide={handleClose} centered size='lg' dialogClassName='upload-modal-dialog'>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ width: 24 }} />
          <h4 style={styles.title}>{adasMapEnabled ? 'New ADAS Report' : 'Upload Estimate'}</h4>
          <button style={styles.closeBtn} onClick={handleClose} aria-label='Close'>
            <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
              <path d='M12 4L4 12M4 4L12 12' stroke='#2A2A2A' strokeWidth='2' strokeLinecap='round' />
            </svg>
          </button>
        </div>

        {/* Step 1: File Upload */}
        <div style={styles.section}>
          <p style={styles.stepLabel}>1. Choose file to upload</p>
          {!file ? (
            <div
              style={styles.dropZone}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              <input
                ref={fileInputRef}
                type='file'
                accept='application/pdf'
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <div style={styles.dropZoneContent}>
                <p style={styles.dropText}>Choose file or drag & drop it here</p>
                <p style={styles.dropSubtext}>
                  {adasMapEnabled
                    ? 'Repair estimate (CCC, Mitchell, Audatex) or ADAS Map report · PDF up to 20MB'
                    : 'CCC / Mitchell / Audatex PDF (up to 20MB)'}
                </p>
              </div>
              <button
                style={styles.browseBtn}
                onClick={(e) => { e.stopPropagation(); fileInputRef.current && fileInputRef.current.click() }}
              >
                Browse File
              </button>
            </div>
          ) : (
            <div style={styles.dropZone}>
              <div style={styles.fileInfo}>
                <span style={styles.fileIcon}>&#128206;</span>
                <span style={styles.fileName}>{file.name}</span>
              </div>
              <p style={styles.fileSize}>{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              <button
                style={styles.browseBtn}
                onClick={() => { setFile(null); setTimeout(() => fileInputRef.current && fileInputRef.current.click(), 100) }}
              >
                Click to Replace
              </button>
              <input
                ref={fileInputRef}
                type='file'
                accept='application/pdf'
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
          )}
        </div>

        {/* Step 2: Select Vendor */}
        <div style={styles.section}>
          <p style={styles.stepLabel}>2. Select vendor</p>
          {vendors.length === 0 && (
            <p style={{ color: '#808B90', fontSize: 16 }}>No vendors available — contact your shop.</p>
          )}
          {vendors.length >= 1 && (
            <FormSelectSearch
              fluid
              className='light'
              size='medium'
              options={vendorOptions}
              value={vendorId}
              onChange={setVendorId}
              placeholder='Choose a vendor...'
              onSearch={() => {}}
              onScroll={() => {}}
            />
          )}
        </div>

        {/* Upload Button */}
        <div style={styles.footer}>
          <Button
            style={{
              ...styles.uploadBtn,
              opacity: canSubmit && !uploading ? 1 : 0.5,
            }}
            disabled={!canSubmit || uploading}
            onClick={handleSubmit}
          >
            {uploading ? <><Spinner animation='border' size='sm' className='me-1' /> Uploading...</> : 'Upload'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

const styles = {
  container: {
    background: '#FFFFFF',
    borderRadius: 16,
    padding: '40px 64px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 40,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  title: {
    fontFamily: "'Source Sans Pro', sans-serif",
    fontWeight: 600,
    fontSize: 24,
    lineHeight: '40px',
    color: '#000',
    textAlign: 'center',
    margin: 0,
  },
  closeBtn: {
    width: 24,
    height: 24,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
  },
  stepLabel: {
    fontFamily: "'Source Sans Pro', sans-serif",
    fontWeight: 400,
    fontSize: 16,
    color: '#000',
    margin: 0,
  },
  dropZone: {
    background: '#FFFFFF',
    border: '2px dashed #007AFF',
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 40px',
    gap: 40,
    cursor: 'pointer',
  },
  dropZoneContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  dropText: {
    fontFamily: "'Source Sans Pro', sans-serif",
    fontWeight: 400,
    fontSize: 18,
    color: '#000',
    margin: 0,
  },
  dropSubtext: {
    fontFamily: "'Source Sans Pro', sans-serif",
    fontWeight: 400,
    fontSize: 16,
    color: '#5D696F',
    margin: 0,
  },
  browseBtn: {
    fontFamily: "'Source Sans Pro', sans-serif",
    fontWeight: 400,
    fontSize: 16,
    color: '#4D5559',
    background: 'none',
    border: '1px solid #CAD2D9',
    borderRadius: 8,
    padding: '8px 16px',
    cursor: 'pointer',
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  fileIcon: {
    fontSize: 18,
  },
  fileName: {
    fontFamily: "'Source Sans Pro', sans-serif",
    fontSize: 16,
    color: '#007AFF',
    fontWeight: 400,
  },
  fileSize: {
    fontFamily: "'Source Sans Pro', sans-serif",
    fontSize: 14,
    color: '#5D696F',
    margin: 0,
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: "'Source Sans Pro', sans-serif",
    fontSize: 16,
    color: '#000',
    cursor: 'pointer',
  },
  noteInput: {
    fontFamily: "'Source Sans Pro', sans-serif",
    fontSize: 16,
    color: '#000',
    border: '1px solid #CAD2D9',
    borderRadius: 8,
    padding: '12px 16px',
    width: '100%',
    resize: 'vertical',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  uploadBtn: {
    fontFamily: "'Source Sans Pro', sans-serif",
    fontWeight: 900,
    fontSize: 14,
    color: '#FFFFFF',
    background: '#007AFF',
    border: 'none',
    borderRadius: 4,
    padding: '12px 24px',
    cursor: 'pointer',
  },
}

export default UploadModal
